import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import type { ContributionByJobFamilyPoint } from "@shared-core/data/api-types";
import {
  formatGbpCompact,
  niceAxisMax,
} from "@shared-core/domain/insights-monthly-revenue";
import {
  jobFamilyLabel,
  pivotContributionByFamily,
  type ContributionByFamilyDatum,
} from "@shared-core/domain/insights-sales-mix";

// Booked contribution by job family, monthly — a stacked bar so the total bar
// height still reads as "total contribution that month" (matching the Monthly
// tab's headline figure) while the segments show the mix shift over time.
// Server composes (the dbt job_family categorisation), client only pivots the
// long-format rows into one stacked-bar row per month and renders.

const FAMILY_COLORS = [
  "var(--color-chart-1)",
  "var(--color-chart-2)",
  "var(--color-chart-3)",
  "var(--color-chart-4)",
  "var(--color-chart-5)",
  "#8b5cf6",
  "#ec4899",
  "#0ea5e9",
];

export interface ContributionByFamilyChartProps {
  points: ContributionByJobFamilyPoint[];
}

export function ContributionByFamilyChart({
  points,
}: ContributionByFamilyChartProps) {
  const { data, families } = pivotContributionByFamily(points);

  if (data.length === 0) {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
        No contribution data for this period yet.
      </div>
    );
  }

  const chartData = data.map((d) => ({ ...d, ...d.byFamily }));
  const totalMax = niceAxisMax(Math.max(0, ...data.map((d) => d.total)));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-foreground text-base font-semibold">
          Contribution by job type — monthly
        </h2>
        <p className="text-muted-foreground text-sm">
          Whole company · booked contribution · trailing {data.length} months
        </p>
      </div>

      <div className="border-border bg-surface rounded-lg border p-4">
        <ChartLegend families={families} />
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
              axisLine={{ stroke: "var(--color-border)" }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis
              domain={[0, totalMax]}
              allowDataOverflow
              tickCount={5}
              tickFormatter={formatGbpCompact}
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <Tooltip
              cursor={{ fill: "var(--color-muted)", opacity: 0.3 }}
              content={
                ((props: TooltipContentProps<number, string>) => (
                  <FamilyTooltip {...props} families={families} />
                )) as never
              }
            />
            {families.map((family, i) => (
              <Bar
                key={family}
                dataKey={family}
                name={jobFamilyLabel(family)}
                stackId="contribution"
                fill={FAMILY_COLORS[i % FAMILY_COLORS.length]}
                isAnimationActive={false}
                fillOpacity={0.95}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ChartLegend({ families }: { families: string[] }) {
  return (
    <div className="text-muted-foreground mb-3 flex flex-wrap items-center gap-4 text-xs">
      {families.map((family, i) => (
        <span key={family} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-3 rounded-sm"
            style={{
              backgroundColor: FAMILY_COLORS[i % FAMILY_COLORS.length],
            }}
          />
          {jobFamilyLabel(family)}
        </span>
      ))}
    </div>
  );
}

function FamilyTooltip(
  props: TooltipContentProps<number, string> & { families: string[] },
) {
  if (!props.active || !props.payload || props.payload.length === 0) {
    return null;
  }
  const datum = props.payload[0]?.payload as
    ContributionByFamilyDatum | undefined;
  if (!datum) return null;
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="mb-1 font-semibold">{datum.label}</div>
      {props.families.map((family, i) => (
        <div key={family} className="flex items-center justify-between gap-4">
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{
                backgroundColor: FAMILY_COLORS[i % FAMILY_COLORS.length],
              }}
            />
            {jobFamilyLabel(family)}
          </span>
          <span className="font-mono tabular-nums">
            {formatGbpCompact(datum.byFamily[family] ?? 0)}
          </span>
        </div>
      ))}
      <div className="text-muted-foreground mt-1 border-t border-[var(--color-border)] pt-1">
        Total {formatGbpCompact(datum.total)}
      </div>
    </div>
  );
}
